/**
 * main.ts — orchestrates the start → running → finished state machine.
 *
 * States:
 *   - start:    StartScreen overlay shown over a dimmed lake. No simulation.
 *   - running:  episode in progress. A rAF loop drives the renderer; a
 *               setInterval fires one bandit round every `config.roundMs`.
 *   - finished: results overlay shown. rAF keeps running so the frozen lake
 *               + heat-map stay visible behind the card; no new rounds fire.
 *
 * The rAF loop runs for the lifetime of the page (cheap; it just interpolates
 * the hook and blits layers). Bandit rounds are gated by the `running` state
 * and capped at `config.rounds`.
 */

import './style.css';
import { Lake } from './environment';
import { LinUcb } from './bandit';
import {
  defaultLayout,
  featurize,
  topSpatial,
  topMarginalValue,
  R_BLOCK,
  G_BLOCK,
  B_BLOCK,
  SIZE_BLOCK,
  type Action,
} from './featurize';
import { Renderer, makeHook, type HookState } from './render';
import { SidePanel, type PanelState } from './ui';
import { StartScreen, type EpisodeConfig } from './start';
import { ResultsScreen, type ResultsStats } from './results';
import { HelpModal, ESSAY_URL } from './help';
import { makeRng, randn } from './linalg';

const CANDIDATES = 60;
const RECENT_WINDOW = 40;
/** Size of the final-window slice used for the headline success-rate. */
const FINAL_WINDOW = 20;

function main(): void {
  const app = document.getElementById('app')!;
  app.innerHTML = '';

  // Persistent layout: lake canvas (left) + side panel (right). The panel
  // is rebuilt per-episode (its state resets), but the lake canvas persists
  // so the renderer keeps a stable target. Overlays mount into lake-wrap.
  const lakeCanvas = document.createElement('canvas');
  lakeCanvas.className = 'lake';
  const lakeWrap = document.createElement('div');
  lakeWrap.className = 'lake-wrap';
  lakeWrap.appendChild(lakeCanvas);
  app.appendChild(lakeWrap);

  // The side-panel slot — emptied and refilled each episode.
  const panelSlot = document.createElement('div');
  panelSlot.className = 'panel-slot';
  app.appendChild(panelSlot);

  // Persistent help button (top-left, always clickable) and footer (bottom
  // center, always visible). Mounted to document.body so they sit above every
  // overlay — start screen, results, running episode.
  const helpBtn = document.createElement('button');
  helpBtn.className = 'help-btn';
  helpBtn.type = 'button';
  helpBtn.textContent = '?';
  helpBtn.setAttribute('aria-label', 'What is this?');
  document.body.appendChild(helpBtn);

  const footer = document.createElement('footer');
  footer.className = 'app-footer';
  footer.innerHTML = `Bandit Fisher © Anton A. Nesterov — <a href="${ESSAY_URL}" target="_blank" rel="noopener">Read Essay</a>`;
  document.body.appendChild(footer);

  let helpModal: HelpModal | null = null;
  function showHelp(): void {
    if (helpModal) return;
    helpModal = new HelpModal(() => {
      helpModal?.remove();
      helpModal = null;
    });
    helpModal.mount(document.body);
  }
  helpBtn.addEventListener('click', showHelp);

  // We need a Lake for the renderer to read preference/flock from even before
  // the episode starts (so the lake renders behind the start card). Reuse a
  // default Lake and replace it when the episode begins.
  let lake = new Lake();
  const layout = defaultLayout();
  let bandit = new LinUcb(layout.M);
  const renderer = new Renderer(lakeCanvas, lake);
  const hook: HookState = makeHook();
  // rAF runs forever; the renderer is a no-op when the bandit has no data.
  const ro = new ResizeObserver(() => renderer.resize());
  ro.observe(lakeCanvas);
  requestAnimationFrame(function frame(t) {
    renderer.frame(t, hook, bandit);
    requestAnimationFrame(frame);
  });

  // ---------------- start state ----------------
  function showStart(): void {
    bandit = new LinUcb(layout.M); // cold bandit so the lake renders clean
    lake = new Lake();
    renderer.setLake(lake);
    panelSlot.innerHTML = '';
    const start = new StartScreen((cfg) => {
      start.remove();
      beginEpisode(cfg);
    });
    start.mount(lakeWrap);
  }

  // ---------------- running → finished ----------------
  function beginEpisode(cfg: EpisodeConfig): void {
    // Fresh world + model with the chosen hyperparameters.
    lake = new Lake({ spatialDrift: cfg.drift, featureDrift: cfg.drift * 0.5 });
    renderer.setLake(lake);
    bandit = new LinUcb(layout.M, { windowN: cfg.windowN, beta: cfg.beta });

    // Fresh side panel for the episode.
    const panel = new SidePanel({
      onEditPreference: (p) => lake.setPreference(p),
    });
    panelSlot.innerHTML = '';
    panelSlot.appendChild(panel.el);
    panel.reset();

    // Hook travel stays tight even at slow round speeds — never longer than
    // 80% of the round, never shorter than 200 ms.
    const hookTravelMs = Math.max(200, Math.min(cfg.roundMs * 0.8, 900));

    let round = 0;
    let catches = 0;
    let casts = 0;
    let weightKg = 0;
    let regretCum = 0;
    let firstCatchRound: number | null = null;
    const recentCatches: boolean[] = [];
    const catchHistory: boolean[] = [];
    const rng = makeRng(lake.opts.seed ^ 0x9e3779b9);

    function sampleCandidates(): { actions: Action[]; features: Float64Array[] } {
      const actions: Action[] = [];
      const nRandom = Math.floor(CANDIDATES / 3);
      const nRefine = Math.floor(CANDIDATES / 3);
      for (let i = 0; i < nRandom; i++) actions.push(randomAction(rng));
      if (bandit.nObs > 0) {
        const pos = topSpatial(bandit.U, layout);
        const guess: Action = {
          x: pos.x,
          y: pos.y,
          r: topMarginalValue(R_BLOCK, bandit.U, layout),
          g: topMarginalValue(G_BLOCK, bandit.U, layout),
          b: topMarginalValue(B_BLOCK, bandit.U, layout),
          size: topMarginalValue(SIZE_BLOCK, bandit.U, layout),
        };
        for (let i = 0; i < nRefine; i++) actions.push(perturb(guess, rng, 0.05));
        const last = hook.action;
        while (actions.length < CANDIDATES) actions.push(perturb(last, rng, 0.1));
      } else {
        while (actions.length < CANDIDATES) actions.push(randomAction(rng));
      }
      const features = actions.map((a) => featurize(a, layout));
      return { actions, features };
    }

    function banditRound(): void {
      round += 1;
      casts += 1;
      const { actions, features } = sampleCandidates();
      const choice = bandit.select(features);
      const a = actions[choice];

      const now = performance.now();
      const start = renderer.currentHookPos(hook, now);
      hook.x = start.x;
      hook.y = start.y;
      hook.targetX = a.x;
      hook.targetY = a.y;
      hook.departMs = now;
      hook.travelMs = hookTravelMs;
      hook.action = { ...a };

      const obs = lake.observe(a);
      bandit.update(features[choice], obs.agentReward);

      if (obs.caught) {
        catches += 1;
        weightKg += obs.weightKg;
        if (firstCatchRound === null) firstCatchRound = round;
      }

      let bestP = 0;
      for (const cand of actions) {
        const p = lake.catchProbability(cand);
        if (p > bestP) bestP = p;
      }
      regretCum += bestP - obs.pCatch;

      recentCatches.push(obs.caught);
      if (recentCatches.length > RECENT_WINDOW) recentCatches.shift();
      catchHistory.push(obs.caught);

      hook.lastCatch = {
        caught: obs.caught,
        match: obs.match,
        atMs: now + hookTravelMs,
      };

      lake.driftPreference();

      renderer.beliefDirty = true;
      const guess = renderer.agentGuessRead;
      const state: PanelState = {
        round,
        cumulativeRegret: regretCum,
        catches,
        casts,
        weightKg,
        recentCatches,
        weights: bandit.U,
        layout,
        agentColor: guess.color,
        agentSize: guess.size,
        truePreference: { ...lake.preference },
        beta: bandit.beta,
      };
      panel.update(state);

      // Stop when we hit the round cap.
      if (round >= cfg.rounds) {
        clearInterval(intervalId);
        finishEpisode();
      }
    }

    let intervalId = window.setInterval(banditRound, cfg.roundMs);

    function finishEpisode(): void {
      const finalStart = Math.max(0, catchHistory.length - FINAL_WINDOW);
      const stats: ResultsStats = {
        rounds: round,
        casts,
        catches,
        weightKg,
        cumulativeRegret: regretCum,
        firstCatchRound,
        finalWindow: catchHistory.slice(finalStart),
      };
      const results = new ResultsScreen(stats, () => {
        results.remove();
        showStart();
      });
      results.mount(lakeWrap);
    }

    // Kick off the first round immediately so the episode starts at once.
    banditRound();
  }

  // Boot: show the start screen.
  showStart();
}

function randomAction(rng: () => number): Action {
  return { x: rng(), y: rng(), r: rng(), g: rng(), b: rng(), size: rng() };
}

function perturb(a: Action, rng: () => number, sigma: number): Action {
  const p = (v: number) => Math.max(0, Math.min(1, v + randn(rng) * sigma));
  return { x: p(a.x), y: p(a.y), r: p(a.r), g: p(a.g), b: p(a.b), size: p(a.size) };
}

main();
